import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AlertsPanel = ({ alerts, onConfigureAlert, onDismissAlert }) => {
  const [activeTab, setActiveTab] = useState('active');

  const alertTabs = [
    { id: 'active', label: 'Active Alerts', count: alerts?.filter(a => a?.status === 'active')?.length },
    { id: 'dismissed', label: 'Dismissed', count: alerts?.filter(a => a?.status === 'dismissed')?.length }
  ];

  const getAlertIcon = (type) => {
    switch (type) {
      case 'ltv_breach':
        return { icon: 'AlertTriangle', color: 'text-destructive' };
      case 'value_drop':
        return { icon: 'TrendingDown', color: 'text-warning' };
      case 'stale_valuation':
        return { icon: 'Clock', color: 'text-accent' };
      case 'high_risk':
        return { icon: 'Shield', color: 'text-destructive' };
      default:
        return { icon: 'Bell', color: 'text-text-secondary' };
    }
  };

  const getAlertSeverity = (severity) => {
    const severityConfig = {
      high: 'bg-destructive text-destructive-foreground',
      medium: 'bg-warning text-warning-foreground',
      low: 'bg-accent text-accent-foreground'
    };
    
    return severityConfig?.[severity] || 'bg-muted text-text-secondary';
  };

  const formatAlertTime = (timestamp) => {
    const now = new Date();
    const alertTime = new Date(timestamp);
    const diffHours = Math.floor((now - alertTime) / (1000 * 60 * 60));
    
    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours}h ago`;
    return alertTime?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const filteredAlerts = alerts?.filter(alert => alert?.status === activeTab);

  return (
    <div className="bg-card border border-border rounded-lg shadow-card">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-text-primary">Portfolio Alerts</h2>
          <Button
            variant="outline"
            size="sm"
            onClick={onConfigureAlert}
            iconName="Settings"
            iconPosition="left"
          >
            Configure
          </Button>
        </div>
      </div>
      {/* Alert Tabs */}
      <div className="flex border-b border-border">
        {alertTabs?.map((tab) => (
          <button
            key={tab?.id}
            onClick={() => setActiveTab(tab?.id)}
            className={`flex items-center space-x-2 px-6 py-3 text-sm font-medium border-b-2 transition-smooth ${
              activeTab === tab?.id
                ? 'border-primary text-primary' :'border-transparent text-text-secondary hover:text-text-primary'
            }`}
          >
            <span>{tab?.label}</span>
            {tab?.count > 0 && (
              <span className="px-2 py-0.5 bg-muted text-text-secondary rounded-full text-xs">
                {tab?.count}
              </span>
            )}
          </button>
        ))}
      </div>
      <div className="p-6">
        {filteredAlerts?.length > 0 ? (
          <div className="space-y-4">
            {filteredAlerts?.map((alert) => {
              const alertIcon = getAlertIcon(alert?.type);
              return (
                <div key={alert?.id} className="flex items-start space-x-4 p-4 bg-muted rounded-lg">
                  <div className={`p-2 rounded-lg bg-surface ${alertIcon?.color}`}>
                    <Icon name={alertIcon?.icon} size={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-medium text-text-primary">{alert?.title}</h4>
                        <p className="text-sm text-text-secondary mt-1">{alert?.message}</p>
                        <div className="flex items-center space-x-3 mt-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getAlertSeverity(alert?.severity)}`}>
                            {alert?.severity?.charAt(0)?.toUpperCase() + alert?.severity?.slice(1)}
                          </span>
                          <span className="text-xs text-text-secondary">
                            {formatAlertTime(alert?.timestamp)}
                          </span>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2 ml-4">
                        {alert?.status === 'active' && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              iconName="Eye"
                              className="text-text-secondary hover:text-text-primary"
                            />
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => onDismissAlert(alert?.id)}
                              iconName="X"
                              className="text-text-secondary hover:text-destructive"
                            />
                          </>
                        )}
                      </div>
                    </div>
                    
                    {alert?.assetDetails && (
                      <div className="mt-3 p-3 bg-surface rounded border border-border">
                        <div className="text-sm">
                          <span className="font-medium text-text-primary">Asset: </span>
                          <span className="text-text-secondary">{alert?.assetDetails?.name}</span>
                        </div>
                        <div className="text-sm mt-1">
                          <span className="font-medium text-text-primary">Current LTV: </span>
                          <span className={`${alert?.assetDetails?.ltv >= 90 ? 'text-destructive' : 'text-text-secondary'}`}>
                            {alert?.assetDetails?.ltv}%
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8">
            <Icon 
              name={activeTab === 'active' ? 'CheckCircle' : 'Archive'} 
              size={32} 
              className="mx-auto mb-2 text-text-secondary opacity-50" 
            />
            <p className="text-text-secondary">
              {activeTab === 'active' ? 'No active alerts' : 'No dismissed alerts'}
            </p>
          </div>
        )}

        {/* Alert Summary */}
        {activeTab === 'active' && filteredAlerts?.length > 0 && (
          <div className="mt-6 pt-6 border-t border-border">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-lg font-semibold text-destructive">
                  {filteredAlerts?.filter(a => a?.severity === 'high')?.length}
                </div>
                <div className="text-xs text-text-secondary">High Priority</div>
              </div>
              <div>
                <div className="text-lg font-semibold text-warning">
                  {filteredAlerts?.filter(a => a?.severity === 'medium')?.length}
                </div>
                <div className="text-xs text-text-secondary">Medium Priority</div>
              </div>
              <div>
                <div className="text-lg font-semibold text-accent">
                  {filteredAlerts?.filter(a => a?.severity === 'low')?.length}
                </div>
                <div className="text-xs text-text-secondary">Low Priority</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AlertsPanel;